import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Search,
  ShieldAlert,
  Clock,
  TrendingDown,
  ArrowRight,
  FileWarning,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

const PERSON_IMAGE =
  'https://customer-assets.emergentagent.com/job_02fd6e9e-f2bc-442e-93dc-7b1dbf31bd01/artifacts/pp6bzju1_ChatGPT%20Image%203%20%D0%BC%D0%B0%D1%80.%202026%20%D0%B3.%2C%2018_39_49.png';

const painPoints = [
  {
    icon: Search,
    title: 'Eligibility Ambiguity',
    description:
      <React.Fragment>Mandatory criteria buried in 200+ page documents.<br className="md:hidden" /> Missed one clause — weeks of work wasted.</React.Fragment>,
  },
  {
    icon: ShieldAlert,
    title: 'Hidden Compliance Traps',
    description:
      'Unusual liability clauses and non-standard insurance requirements go unnoticed until submission.',
  },
  {
    icon: Clock,
    title: 'Last-Minute Discoveries',
    description:
      'Teams discover gaps days before deadline, triggering rushed addendums and incomplete responses.',
  },
  {
    icon: TrendingDown,
    title: 'No Bid Intelligence',
    description:
      <React.Fragment>Without data, every tender looks equally winnable.<br className="md:hidden" /> Teams pursue bad fits and miss real opportunities.</React.Fragment>,
  },
];

export default function Problem() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="problem"
      data-testid="problem-narrative-section"
      className="section-spacing scroll-mt-24"
    >
      <div className="section-container">
        {/* Contained rounded card */}
        <div
          data-animate
          className="rounded-none overflow-hidden"
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left: Copy + 2x2 features */}
            <div className="flex flex-col">
              <h2
                className="font-display text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] leading-[1] lg:leading-[1.1] mb-5"
              >
                Tender Processes Are<br />Designed for Failure.
              </h2>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-10">
                Every year, procurement teams spend thousands of hours reviewing documents, cross-referencing requirements, and building compliance matrices — yet 23% of bids still fail on technicalities.
              </p>

              {/* 2x2 features grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {painPoints.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title}>
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-secondary border border-border/50 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-foreground/70" />
                        </div>
                        <h3 className="text-sm font-semibold text-foreground">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <div className="mt-auto">
                <Button
                  data-testid="problem-cta-button"
                  onClick={() => scrollTo('#waitlist')}
                  className="btn-lift bg-[hsl(150,18%,22%)] text-white hover:bg-[hsl(150,18%,26%)] h-11 px-6 text-sm font-medium rounded-full shadow-card"
                >
                  See How It Works
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Right: Photo with floating UI elements */}
            <div className="relative rounded-[20px] overflow-hidden min-h-[420px] lg:min-h-[520px]">
              {/* Background photo */}
              <img
                src={PERSON_IMAGE}
                alt="Professional reviewing tender documents"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Subtle overlay for readability */}
              <div className="absolute inset-0 bg-gradient-to-tl from-black/20 via-transparent to-transparent" />

              {/* Bottom-right: AI insight card */}
              <div
                className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 w-[200px]"
                data-animate="slide-right"
                style={{ transitionDelay: '350ms' }}
              >
                <div className="bg-white/90 backdrop-blur-md rounded-xl p-4 shadow-float border border-white/60">
                  <p className="text-xs font-semibold text-foreground mb-2.5">AI Insight</p>
                  <div className="bg-secondary rounded-lg px-3 py-1.5 mb-3">
                    <p className="text-[10px] font-medium text-foreground/80">Risk Score</p>
                  </div>
                  {/* Gauge */}
                  <div className="flex items-center justify-center">
                    <div className="relative w-24 h-14">
                      {/* Semi-circle gauge */}
                      <svg viewBox="0 0 100 55" className="w-full h-full">
                        {/* Background arc */}
                        <path
                          d="M 10 50 A 40 40 0 0 1 90 50"
                          fill="none"
                          stroke="hsl(36 22% 93%)"
                          strokeWidth="6"
                          strokeLinecap="round"
                        />
                        {/* Filled arc (23% of 180deg) */}
                        <path
                          d="M 10 50 A 40 40 0 0 1 90 50"
                          fill="none"
                          stroke="hsl(150 18% 22%)"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray="126"
                          strokeDashoffset="30"
                        />
                        {/* Dashed decorative arc */}
                        <path
                          d="M 5 50 A 45 45 0 0 1 95 50"
                          fill="none"
                          stroke="hsl(30 14% 86%)"
                          strokeWidth="1"
                          strokeDasharray="3 4"
                        />
                      </svg>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                        <span className="font-display text-xl text-foreground">23%</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-[10px] text-muted-foreground mt-1">Fail Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
